import { SB } from '@play-co/replicant';

/**
 * State schema for features that have been removed from the game.
 *
 * Removing a state property takes two releases (see "State Migrations" in
 * `.claude/rules/replicant.md`): the first drops all code references and keeps
 * the schema field marked `@deprecated`, the second removes the field and adds
 * a cleanup migration. This file is where fields live during that window, so
 * `State.ts` only ever lists schemas for features that still exist.
 *
 * Dropping a field from the schema without a cleanup migration is not a
 * shortcut: `VirtualState` hides it from game-facing state but preserves it in
 * persisted state, so the dead bytes keep counting against per-state storage
 * limits forever.
 *
 * This is the state-side counterpart to
 * `src/replicant/config/messages/deprecatedMessages.ts`, which keeps removed
 * messages registered as NOOPs.
 *
 * When adding here: copy the original schema verbatim, including `.optional()`
 * markers and nesting. Any drift can corrupt or reject live player state.
 * When a cleanup migration ships, delete the entry from this file in the same
 * release.
 */

// Boost Rocket ran three live events (2026-04-20, 2026-04-27, 2026-05-18), so
// player states in the wild still carry this subtree.
const boostRocketPlayerSchema = SB.object({
  score: SB.int(),
  status: SB.tuple(['active', 'inviteSent', 'inviteReceived'] as const),
  updatedAt: SB.int(),
  lastScoreAdded: SB.int().optional(),
});

const boostRocketEventSchema = SB.object({
  party: SB.map(boostRocketPlayerSchema),
  invites: SB.map(boostRocketPlayerSchema),
  score: SB.int(),
  lastScoreAdded: SB.int().optional(),
  seenAt: SB.int(),
  rewardsClaimed: SB.boolean(),
  milestonesClaimed: SB.map(SB.boolean()).optional(),
  ftueSeenAt: SB.int().optional(),
  bankBalance: SB.int(),
  lastBankRefillAt: SB.int(),
  seedGranted: SB.boolean(),
  declinedInvites: SB.map(SB.boolean()).optional(),
  lastSeenPartySize: SB.int().optional(),
});

// Fairy Forest (user-facing "Firefly Garden") ran six live 10-day events
// between 2025-06-27 and 2025-12-23, so player states in the wild still carry
// this subtree.
const fairyForestPlant = SB.object({
  /** Partner user id */
  partnerId: SB.string(),
  /** Partner user name */
  partnerName: SB.string(),
  /** Partner profile picture */
  partnerPhoto: SB.string(),
  /** Partner profile frame */
  partnerFrame: SB.string().default('default'),
  /** Partner score in this plant */
  partnerScore: SB.number(),
  /** User score in this plant */
  score: SB.number(),
  /** Milestones claimed by the user */
  claimedMilestones: SB.number(),
  /** Timestamp for first time user sees this plant, used for RDN */
  seenAt: SB.number().optional(),
  /** Timestamp for when the plant has started */
  createdAt: SB.number().optional(),
});

const fairyForestInvite = SB.object({
  /** The name of the other player */
  name: SB.string(),
  /** The profile picture of the other player */
  photo: SB.string(),
  /** The profile frame of the other player */
  frame: SB.string().default('default'),
  /** Timestamp of when the invite was sent */
  sentAt: SB.number(),
});

const fairyForestEntry = SB.object({
  /** All plants that user is playing */
  plants: SB.map(fairyForestPlant),
  /** Number of magic balls available */
  balls: SB.number(),
  /** Timestamp of when the entry state is created - can happen before startedAt */
  joinedAt: SB.number(),
  /** Timestamp of when user claimed the grand prize of this event */
  claimedAt: SB.number().optional(),
  /** All invites sent to other users */
  invitesSent: SB.map(fairyForestInvite),
  /** All invites received from other users */
  invitesReceived: SB.map(fairyForestInvite),
  /** List of user IDs with declined invites, either by the user or the other player */
  invitesBlacklist: SB.array(SB.string()),
  /** Number of invites user has sent */
  numInvitesSent: SB.number(),
  /** Number of invites user has received */
  numInvitesReceived: SB.number(),
  /** Number of invites user has accepted */
  numInvitesAccepted: SB.number(),
  /** Number of invites user has declined */
  numInvitesDeclined: SB.number(),
  /** Timestamp of when user actually started playing the event */
  startedAt: SB.number().optional(),
  /** Timestamp of last time bots have been updated */
  botsUpdatedAt: SB.number().optional(),
  /** Cumulative number of the balls user has played in this event */
  ballsPlayed: SB.number().optional(),
});

export const deprecatedStateSchema = {
  /** @deprecated ChampionsLeagueV2 removed (#10056); kept for state compatibility. */
  championsLeagueWinStreak: SB.number().default(0),
  /** @deprecated ChampionsLeagueV2 removed (#10056); kept for state compatibility. */
  championsLeaguePreviousTier: SB.string().optional(),

  /** @deprecated Boost Rocket removed (#11759); kept for state compatibility. */
  boostRocket: SB.object({
    events: SB.map(boostRocketEventSchema),
  }).optional(),

  /** @deprecated Fairy Forest removed (#11760); kept for state compatibility. */
  fairyForest: SB.object({
    /** Map of events that user has participated in */
    entries: SB.map(fairyForestEntry),
    /** Last time user has seen the Fairy Forest feature */
    lastSeenAt: SB.int().optional(),
    /** Map of the completed tutorial steps */
    tutorialSteps: SB.map(SB.boolean()).optional(),
    /** Cumulative number of the balls user has played all time */
    allTimeBallsPlayed: SB.number(),
  }).optional(),
};
